import React from "react";
import { AlertCircle } from "lucide-react";
import "../CustomTheme/customTheme.css";
import "./CustomFormField.css";

/**
 * Ids for linking a control to its hint / error text (aria-describedby).
 *   const ids = getFieldIds(id);
 *   <input id={id} aria-describedby={ids.describedBy(error, hint)} />
 */
export const getFieldIds = (id) => ({
  hintId: id ? `${id}-hint` : undefined,
  errorId: id ? `${id}-error` : undefined,
  // the hint is hidden while an error shows, so only one of them is ever referenced
  describedBy: (error, hint) =>
    (id && (error ? `${id}-error` : hint ? `${id}-hint` : null)) || undefined,
});

/**
 * CustomFormField
 * ---------------
 * The shared frame around every form control: label, required "*",
 * the control itself (children), hint text and the red error message.
 * All Custom* inputs use it internally; use it directly to wrap any
 * control that has no Custom* version yet.
 *
 * Props:
 *  - label        field label
 *  - isRequired   shows a red "*"
 *  - htmlFor      id of the control (links the label + error to it)
 *  - fieldId      id for the hint / error when there is no single control
 *                 to point the label at (e.g. a checkbox group); defaults to htmlFor
 *  - hint         grey help text under the control (hidden while an error shows)
 *  - error        red error message under the control
 *  - colClass     optional bootstrap column wrapper, e.g. "col-md-4"
 *  - layout       "column" (label above, default) | "row" (label beside, md+ only)
 *  - noMargin     removes the bottom spacing
 *  - className, style
 */
const CustomFormField = ({
  label,
  isRequired,
  htmlFor,
  fieldId,
  hint,
  error,
  colClass,
  layout = "column",
  noMargin = false,
  className,
  style,
  children,
}) => {
  const ids = getFieldIds(fieldId || htmlFor);

  const field = (
    <div
      className={`cff-field cff-${layout}${noMargin ? " cff-noMargin" : ""}${
        error ? " cff-hasError" : ""
      }${className ? ` ${className}` : ""}`}
      style={style}
    >
      {label && (
        <label htmlFor={htmlFor} className="cff-label">
          {label}
          {isRequired && (
            <span className="cff-required" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div className="cff-body">
        {children}

        {error ? (
          <span id={ids.errorId} className="cff-error" role="alert">
            <AlertCircle size={13} aria-hidden="true" />
            <span>{error}</span>
          </span>
        ) : (
          hint && (
            <span id={ids.hintId} className="cff-hint">
              {hint}
            </span>
          )
        )}
      </div>
    </div>
  );

  // No colClass -> no wrapper div, so it never takes grid space it wasn't asked for.
  return colClass ? <div className={colClass}>{field}</div> : field;
};

export default CustomFormField;
